import { EntityId } from "@reduxjs/toolkit";
import LoadingButton from "@mui/lab/LoadingButton";
import {
    useGetPortActiveStateQuery,
    useUpdatePortActiveStateMutation,
} from "services/streamingApi/siteCommands.api";
import { useAppSelector } from "app/hooks";
import { selectLiveStreamingConfiguration } from "features/config/store/selectors";
import { useEffect } from "react";
import { enqueueSnackbar } from "notistack";
import { unwrappableActionSnackbarNotificationDecorator } from "utils/unwrappableActionSnackbarNotificationDecorator";
import { Port } from "types/Port";
import { useConfirm } from "material-ui-confirm";
import { useStateTtl } from "hooks/useStateTtl";

const generateConfirmationDescription = (
    templateString: string,
    displayName: string,
    isPortStateActive: boolean | undefined,
) => {
    return templateString
        .replaceAll("{displayName}", displayName)
        .replaceAll(
            "{portStateAction}",
            !isPortStateActive ? "activate" : "deactivate",
        );
};

export interface PortControlProps {
    siteId: EntityId;
    port: Port;
    activeIcon: React.ReactNode;
    inactiveIcon: React.ReactNode;
}

export function PortControl(props: PortControlProps) {
    const liveStreamingConfiguration = useAppSelector(
        selectLiveStreamingConfiguration,
    );

    const [updatePortState, { isLoading: isLoadingUpdatePortState }] =
        useUpdatePortActiveStateMutation();

    // Use a frequent port action polling interval while update action is running, afterwards we fall back to the configured interval
    const portStatePollingIntervalInMs = isLoadingUpdatePortState
        ? 1500
        : liveStreamingConfiguration?.portActiveStateRefreshIntervalInMs;

    // Only notify user of a failed port state retrieval once every 5 minutes
    const { setState: markAsNotified, value: notifiedInTimeWindow } =
        useStateTtl<boolean>(false, 60 * 5);

    const {
        data: isPortStateActive,
        isLoading: isLoadingPortState,
        isError: isLoadingPortStateError,
    } = useGetPortActiveStateQuery(
        {
            siteId: props.siteId,
            portName: props.port.name,
        },
        {
            pollingInterval: portStatePollingIntervalInMs,
        },
    );

    useEffect(() => {
        if (isLoadingPortStateError && !notifiedInTimeWindow) {
            enqueueSnackbar(
                `Failed to load state for port ${props.port.displayName}`,
                {
                    variant: "error",
                },
            );

            markAsNotified(true);
        }
    }, [isLoadingPortStateError, props.port.displayName]);

    const confirm = useConfirm();

    const showBeforeActionInfoNotification = () => {
        enqueueSnackbar(
            `${!isPortStateActive ? "Activating" : "Deactivating"} ${props.port.displayName}`,
            {
                variant: "info",
            },
        );
    };

    const triggerPort = () => {
        showBeforeActionInfoNotification();

        unwrappableActionSnackbarNotificationDecorator(
            updatePortState({
                siteId: props.siteId,
                activate: !isPortStateActive,
                portName: props.port.name,
            }),
            `Successfully ${!isPortStateActive ? "activated" : "deactivated"} ${props.port.displayName}`,
            `Failed to ${!isPortStateActive ? "activate" : "deactivate"} ${props.port.displayName}`,
        );
    };

    const descriptionTemplate: string =
        props.port.confirmationMessageTemplate ??
        "Are you sure you want to {portStateAction} {displayName}";
    const confirmDescription = generateConfirmationDescription(
        descriptionTemplate,
        props.port.displayName,
        isPortStateActive,
    );

    return (
        <LoadingButton
            sx={{
                "&.Mui-disabled": {
                    color: "#2196f3",
                },
            }}
            variant={isPortStateActive ? "outlined" : "contained"}
            color={isPortStateActive ? "success" : "error"}
            startIcon={
                isPortStateActive ? props.activeIcon : props.inactiveIcon
            }
            endIcon={<></>} // Workaround, without this we get a warning: https://github.com/mui/material-ui/issues/31235
            loading={isLoadingPortState || isLoadingUpdatePortState}
            loadingPosition="end"
            disabled={isLoadingPortStateError || isLoadingUpdatePortState}
            disableRipple={isLoadingUpdatePortState}
            disableFocusRipple={isLoadingUpdatePortState}
            onClick={() => {
                // Check if we first need to ask for a confirmation or that we can directly trigger the port
                if (props.port.confirmationRequired) {
                    confirm({
                        description: confirmDescription,
                        confirmationText: "Yes",
                        cancellationText: "No",
                    }).then((confirmResult) => {
                        if (confirmResult.confirmed) {
                            triggerPort();
                        }
                    });
                } else {
                    triggerPort();
                }
            }}
        >
            {props.port.displayName}
        </LoadingButton>
    );
}
